import React, { useMemo, useState } from 'react';
import { Activity, CheckCircle2, Clock, Truck } from 'lucide-react';
import { buildStatusTimeline, formatDateTime } from '../../../../../features/orderDetails/orderUtils';

/**
 * The status timeline, read-only. Editing an event's detail and the source of
 * each event belong to the admin: `update_status_history/` is `IsAdminUser`.
 */
const OrderHistoryTab = ({ order }) => {
  const [previewImage, setPreviewImage] = useState(null);

  const timeline = useMemo(() => buildStatusTimeline(order), [order]);

  /**
   * Icon for the milestone the event represents. Keyed by the stored status
   * rather than by its label, so it keeps working if a label is reworded.
   */
  const getStatusIcon = (status) => {
    if (status === 'delivered' || status === 'withdrawn') return <CheckCircle2 size={16} />;
    if (['in_transit', 'on_the_way', 'in_cd', 'dropped_at_hub', 'received_at_hub'].includes(status)) {
      return <Truck size={16} />;
    }
    if (['pending', 'created_on_courier', 'in_preparation'].includes(status)) {
      return <Clock size={16} />;
    }
    return <Activity size={16} />;
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
      <div
        data-testid="order-history-timeline"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden p-8"
      >
        <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-8 flex items-center gap-2">
          <Activity className="text-ninja-green" size={20} /> Historial de Estados
        </h3>

        {timeline.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
              <Activity size={24} className="text-text-muted" />
            </div>
            <h4 className="text-lg font-bold text-ninja-dark dark:text-white mb-2">Sin movimientos</h4>
            <p className="text-text-secondary max-w-sm">
              Esta orden todavía no registra cambios de estado.
            </p>
          </div>
        ) : (
          <div className="relative pl-6 border-l-2 border-border space-y-8">
            {timeline.map((event) => (
              <div
                key={`${event.id}-${event.date}`}
                data-testid="order-history-event"
                data-status={event.status}
                className="relative"
              >
                <div className="absolute -left-[35px] top-0 w-8 h-8 rounded-full bg-white dark:bg-[#1a1a1a] border-2 border-ninja-green text-ninja-green flex items-center justify-center">
                  {getStatusIcon(event.status)}
                </div>

                <h4 className="text-sm font-bold text-ninja-dark dark:text-white uppercase tracking-wide flex items-center gap-2">
                  {event.statusLabel}
                  {event.substatusLabel && (
                    <span className="text-text-secondary normal-case font-medium">
                      : {event.substatusLabel}
                    </span>
                  )}
                </h4>

                <p className="text-text-secondary text-sm mt-1">
                  {event.details ? `Detalles entrega: ${event.details}` : 'Actualización de estado'}
                </p>

                <p className="text-xs font-semibold text-text-muted mt-2">
                  {formatDateTime(event.date)}
                </p>

                {event.imageUrls.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {event.imageUrls.map((url) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setPreviewImage(url)}
                        className="w-12 h-12 rounded-lg overflow-hidden border border-border hover:border-ninja-green transition-colors"
                      >
                        <img src={url} alt="Evidencia de entrega" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Evidencia de entrega"
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default OrderHistoryTab;
